import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import DataTable, {type IDataTableProps} from "./DataTable";
import useDialogFocus from "./useDialogFocus";
import {api} from "../api";

interface KycPanelProps extends IDataTableProps {
  canReview: boolean;
  onReview: (record: Record<string, any>, status: "verified" | "rejected", reviewComment: string) => Promise<void>;
}

export default function KycPanel({canReview, onReview, ...tableProps}: KycPanelProps) {
  const {t} = useTranslation();
  const [review, setReview] = useState<{record: Record<string, any>; status: "verified" | "rejected"} | null>(null);
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const draftKey = useMemo(() => review
    ? `admin:kyc-review:${String(review.record._id || review.record.id || review.record.userId || "unknown")}:${review.status}`
    : "", [review]);
  const dirty = Boolean(comment.trim());

  const selfieFileId = review?.record?.data?.profile?.accountVerification?.selfieDocumentPhoto?.fileId;
  const selfieMimeType = review?.record?.data?.profile?.accountVerification?.selfieDocumentPhoto?.mimeType;
  const [documentUrl, setDocumentUrl] = useState("");
  const [documentError, setDocumentError] = useState("");
  const [documentLoading, setDocumentLoading] = useState(false);

  useEffect(() => {
    setDocumentUrl("");
    setDocumentError("");
    if (!selfieFileId) return;
    let cancelled = false;
    setDocumentLoading(true);
    api.get(`/data/personal_data/verification/documents/${selfieFileId}/download-url`)
      .then(response => { if (!cancelled) setDocumentUrl(response.data?.downloadUrl || ""); })
      .catch(() => { if (!cancelled) setDocumentError(t("kyc.documentUnavailable")); })
      .finally(() => { if (!cancelled) setDocumentLoading(false); });
    return () => { cancelled = true; };
  }, [selfieFileId, t]);

  useEffect(() => {
    if (!draftKey) return;
    const saved = localStorage.getItem(draftKey);
    if (saved !== null) setComment(saved);
  }, [draftKey]);

  useEffect(() => {
    if (!draftKey) return;
    if (comment) localStorage.setItem(draftKey, comment);
    else localStorage.removeItem(draftKey);
  }, [comment, draftKey]);

  useEffect(() => {
    if (!review) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => {
      window.removeEventListener("beforeunload", beforeUnload);
    };
  });

  const close = () => {
    if (dirty && !window.confirm(t("forms.discardChanges"))) return;
    setReview(null);
    setComment("");
    setError("");
  };
  const dialogRef = useDialogFocus<HTMLFormElement>(Boolean(review), close);
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!review || (review.status === "rejected" && !comment.trim())) { setError(t("kyc.commentRequired")); return; }
    setSaving(true); setError("");
    try {
      await onReview(review.record, review.status, comment.trim());
      if (draftKey) localStorage.removeItem(draftKey);
      setReview(null); setComment("");
    }
    catch (requestError: any) { setError(requestError?.response?.data?.message || t("unableLoad")); }
    finally { setSaving(false); }
  };
  return <>
    <DataTable {...tableProps} defaultColumns={["userId", "verificationStatus", "submittedAt", "reviewedAt"]}
      rowActions={record => canReview && record.verificationStatus === "pending" ? [
        {id: "approve", label: t("kyc.approve"), icon: "check"},
        {id: "reject", label: t("kyc.reject"), icon: "delete", isDanger: true},
      ] : []}
      onRowAction={(action, record) => { setComment(""); setError(""); setReview({record, status: action === "approve" ? "verified" : "rejected"}); }}/>
    {review && <div className="modal-backdrop" role="presentation" onMouseDown={event => { if (event.target === event.currentTarget) close(); }}><form ref={dialogRef} tabIndex={-1} className="modal kyc-review" role="dialog" aria-modal="true" onSubmit={submit}>
      <button type="button" className="modal-close" title={t("administrators.close")} aria-label={t("administrators.close")} onClick={close}>×</button>
      <span className="eyebrow">KYC</span><h2>{review.status === "verified" ? t("kyc.approve") : t("kyc.reject")}</h2>
      {error && <div className="alert" role="alert">{error}</div>}
      {selfieFileId && (
        <div className="kyc-document-preview">
          {documentLoading && <span>{t("kyc.documentLoading")}</span>}
          {documentError && <span className="alert" role="alert">{documentError}</span>}
          {documentUrl && (
            String(selfieMimeType || "").startsWith("image/")
              ? <img src={documentUrl} alt={t("kyc.selfieDocumentPhoto")} className="kyc-document-preview__image"/>
              : <a href={documentUrl} target="_blank" rel="noreferrer">{t("kyc.viewDocument")}</a>
          )}
        </div>
      )}
      <label>{t("kyc.reviewComment")}<textarea value={comment} onChange={event => setComment(event.target.value)} required={review.status === "rejected"}/></label>
      <div className="modal-actions"><button type="button" className="secondary" onClick={close}>{t("forms.cancel")}</button><button type="submit" className={review.status === "rejected" ? "danger-button" : "primary"} disabled={saving || (review.status === "rejected" && !comment.trim())}>{t("forms.confirm")}</button></div>
    </form></div>}
  </>;
}
